import { forwardRef } from 'react';
import Close from '@mui/icons-material/Close';
import styled, { css } from 'styled-components';
import { transparentize } from 'color2k';
import { Colors } from 'styles';
import { Chip } from 'common/ui/Chip';
import type { ChipSize } from 'common/ui/Chip';
import { MacroPill } from './MacroPill';

export type MacroChipProps = {
  size?: ChipSize;
  macroLabel?: React.ReactNode;
  onRemove?: () => void;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isSelected?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  disableOverlay?: boolean;
};

export const MacroChip = forwardRef<HTMLButtonElement, MacroChipProps>(function ForwardedMacroChip(
  {
    size = 'md',
    macroLabel = 'Macro',
    onRemove,
    onClick,
    onMouseDown,
    isSelected = false,
    disabled = false,
    className,
    children,
    disableOverlay = true,
    ...rest
  },
  ref
) {
  const canRemove = !disabled && onRemove != null;
  const handleRemove = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    onRemove?.();
  };

  return (
    <BaseMacroChip
      {...rest}
      ref={ref}
      $size={size}
      $isSelected={isSelected}
      variant="outline"
      color="gray"
      size={size}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={onMouseDown}
      className={className}
      disableOverlay={disableOverlay}
    >
      <MacroPill label={macroLabel} size={size} />
      <MacroLabel>{children}</MacroLabel>
      <RemoveIcon $clickable={canRemove} $size={size}>
        <CloseIcon
          $size={size}
          data-testid="macro-chip-remove"
          onClick={canRemove ? handleRemove : undefined}
        />
      </RemoveIcon>
    </BaseMacroChip>
  );
});

const CHIP_SIZE = {
  xs: css`
    padding: 2px 6px 2px 2px;
    gap: 0.4rem;
  `,
  sm: css`
    padding: 6px 12px 6px 4px;
    gap: 0.5rem;
  `,
  md: css`
    padding: 6px 12px 6px 6px;
    gap: 0.6rem;
  `,
  lg: css`
    padding: 6px 12px 6px 8px;
    gap: 0.7rem;
  `,
} as const;

const REMOVE_SIZE = {
  xs: css`
    width: 1.4rem;
    height: 1.4rem;
  `,
  sm: css`
    width: 1.6rem;
    height: 1.6rem;
  `,
  md: css`
    width: 1.8rem;
    height: 1.8rem;
  `,
  lg: css`
    width: 2rem;
    height: 2rem;
  `,
} as const;

const CLOSE_ICON_SIZE = {
  xs: css`
    width: 1rem;
    height: 1rem;
  `,
  sm: css`
    width: 1.1rem;
    height: 1.1rem;
  `,
  md: css`
    width: 1.2rem;
    height: 1.2rem;
  `,
  lg: css`
    width: 1.3rem;
    height: 1.3rem;
  `,
} as const;

const selectedMacroChipStyles = css`
  border-color: ${Colors.blue5};
  box-shadow: ${transparentize(Colors.blue5, 0.88)} 0 0 0 4px;
`;

const BaseMacroChip = styled(Chip)<{ $size: ChipSize; $isSelected: boolean }>`
  && {
    background: ${transparentize(Colors.gray2, 0.2)};
    border: 1px solid ${transparentize(Colors.gray8, 0.7)};
    border-radius: 999px;
    ${({ $size }) => CHIP_SIZE[$size]}
    ${({ $isSelected }) => $isSelected && selectedMacroChipStyles}
  }

  &:focus,
  &:focus-within {
    ${selectedMacroChipStyles}
  }
`;

const MacroLabel = styled.span`
  font-size: inherit;
  line-height: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const RemoveIcon = styled.span<{ $clickable: boolean; $size: ChipSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $size }) => REMOVE_SIZE[$size]}
  border-radius: 999px;
  background: ${transparentize(Colors.gray9, 0.25)};
  color: ${Colors.gray2};
  flex-shrink: 0;

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        background: ${Colors.gray9};
      }

      &:active {
        background: ${transparentize(Colors.gray9, 0.15)};
      }
    `}
`;

const CloseIcon = styled(Close)<{ $size: ChipSize }>`
  && {
    ${({ $size }) => CLOSE_ICON_SIZE[$size]}
  }
`;
